import { useState } from 'react';
import { Button, Col, Form, Image, Input, Modal, message } from 'antd';
import { TextService } from '../../Services/TextService';
import { BookyInput } from '../../Components/Forms/BookyInput';
import { OneFrontHtmlEditor } from '../../Components/Forms/OneFrontHtmlEditor';
import { BookyCheckbox } from '../../Components/Forms/BookyCheckbox';
import { EmailTemplateService } from '../../Services/EmailTemplateService';
import { EditOutlined } from '@ant-design/icons';
import { EmailTemplateType } from '../../Types/EmailTemplate';
import ImageSelect from '../../Components/Select/ImageSelect';
import EmailDesignTemplateCardSelect from '../../Components/Select/EmailDesignTemplateCardSelect';
import { BlobDto } from '../../Repository/eventbookyapi';
import { DocumentService } from '../../Services/ImageService';
import ColorPicker from '../../Components/Color/ColorPicker';
import { AvailableEmailPurpose } from '../../Mappers/mapAvailablePurpose';
import { EmailTemplatePurpose } from '../../Types/EmailTemplatePurpose';
import { BookySelectDropDown } from '../../Components/Forms/BookySelectDropDown';

const DEFAULT_ACCENT_COLOR = "#4a6cf7";
const DEFAULT_TEXT_COLOR = "#ffffff";

const resolveEmailDesignTemplate = (template: any): EmailTemplateType | undefined => {
    const hasHeaderDesign = !!(template?.header || template?.accentColor || template?.textColor);
    const hasLogoDesign = !!template?.imageUrl;
    const templateType = template?.emailDesignTemplate as EmailTemplateType | undefined;

    // Prefer concrete field data because older clients could save shifted enum values.
    if (hasHeaderDesign) {
        return EmailTemplateType.Header;
    }

    if (hasLogoDesign) {
        return EmailTemplateType.Classic;
    }

    if (templateType === EmailTemplateType.Header) {
        return EmailTemplateType.Header;
    }

    if (templateType === EmailTemplateType.Minimal) {
        return EmailTemplateType.Minimal;
    }

    if (templateType === EmailTemplateType.Classic) {
        // Classic without logo is most likely old Minimal data saved with wrong enum index.
        return EmailTemplateType.Minimal;
    }

    return undefined;
}

export const EditEmailTemplate = (props: any) => {
    const [form] = Form.useForm();
    const [open, setOpen] = useState(false);
    const [initialHtml, setInitialHtml] = useState("" as string);
    const [images, setImages] = useState([] as BlobDto[]);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false as boolean);
    const [selectedLogo, setSelectedLogo] = useState("" as string);
    const [accentColor, setAccentColor] = useState(DEFAULT_ACCENT_COLOR as string);
    const [textColor, setTextColor] = useState(DEFAULT_TEXT_COLOR as string);
    const [purpose, setPurpose] = useState(undefined as EmailTemplatePurpose | undefined);

    const GetText = TextService.GetText;
    const hasEmailDesign = Form.useWatch("enableEmailDesign", form) === true;
    const selectedEmailDesignTemplate = Form.useWatch("emailDesignTemplate", form) as EmailTemplateType | undefined;

    const onCancel = () => {
        clearForm();
        setOpen(false);
    };

    const onHtmlChange = (value: string) => {
        form.setFieldValue("body", value);
    }

    const loadImages = async () => {
        const publicImages = await DocumentService.GetPublicImages();
        setImages(publicImages);
    }

    const openImagePicker = async () => {
        await loadImages();
        setIsImageModalOpen(true);
    }

    const onImageSelect = (image: string) => {
        setSelectedLogo(image);
        form.setFieldValue("imageUrl", image);
    }

    const onEmailDesignToggle = (event: any) => {
        if (event?.target?.checked !== true) {
            form.setFieldValue("emailDesignTemplate", undefined);
            form.setFieldValue("header", undefined);
            form.setFieldValue("imageUrl", undefined);
            form.setFieldValue("accentColor", undefined);
            form.setFieldValue("textColor", undefined);
            setSelectedLogo("");
            setAccentColor(DEFAULT_ACCENT_COLOR);
            setTextColor(DEFAULT_TEXT_COLOR);
            return;
        }

        form.setFieldValue("emailDesignTemplate", EmailTemplateType.Classic);
    }

    const onEmailDesignTemplateChange = (templateType: EmailTemplateType) => {
        if (templateType !== EmailTemplateType.Classic) {
            form.setFieldValue("imageUrl", undefined);
            setSelectedLogo("");
        }

        if (templateType !== EmailTemplateType.Header) {
            form.setFieldValue("header", undefined);
            form.setFieldValue("accentColor", undefined);
            form.setFieldValue("textColor", undefined);
            setAccentColor(DEFAULT_ACCENT_COLOR);
            setTextColor(DEFAULT_TEXT_COLOR);
        }

        if (templateType === EmailTemplateType.Header) {
            const initialAccentColor = form.getFieldValue("accentColor") || DEFAULT_ACCENT_COLOR;
            const initialTextColor = form.getFieldValue("textColor") || DEFAULT_TEXT_COLOR;
            form.setFieldValue("accentColor", initialAccentColor);
            form.setFieldValue("textColor", initialTextColor);
            setAccentColor(initialAccentColor);
            setTextColor(initialTextColor);
        }
    }

    const onAccentColorChanged = (hex: string) => {
        setAccentColor(hex);
        form.setFieldValue("accentColor", hex);
    }

    const onTextColorChanged = (hex: string) => {
        setTextColor(hex);
        form.setFieldValue("textColor", hex);
    }

    const setFieldsFromProps = () => {
        form.setFieldValue("templateName", props.template.templateName);
        form.setFieldValue("subject", props.template.subject);
        setInitialHtml(props.template.body);
        form.setFieldValue("body", props.template.body);

        if (props.template.purpose > 0) {
            form.setFieldValue("purpose", props.template.purpose);
            setPurpose(props.template.purpose);
        }

        form.setFieldValue("notificationEmail", props.template.notificationEmail);

        const resolvedTemplateType = resolveEmailDesignTemplate(props.template);

        if (resolvedTemplateType !== undefined) {
            form.setFieldValue("enableEmailDesign", true);
            form.setFieldValue("emailDesignTemplate", resolvedTemplateType);

            if (resolvedTemplateType === EmailTemplateType.Classic) {
                form.setFieldValue("imageUrl", props.template.imageUrl);
                setSelectedLogo(props.template.imageUrl || "");
            } else {
                form.setFieldValue("imageUrl", undefined);
                setSelectedLogo("");
            }

            if (resolvedTemplateType === EmailTemplateType.Header) {
                const initialAccentColor = props.template.accentColor || DEFAULT_ACCENT_COLOR;
                const initialTextColor = props.template.textColor || DEFAULT_TEXT_COLOR;

                form.setFieldValue("header", props.template.header);
                form.setFieldValue("accentColor", initialAccentColor);
                form.setFieldValue("textColor", initialTextColor);

                setAccentColor(initialAccentColor);
                setTextColor(initialTextColor);
            } else {
                form.setFieldValue("header", undefined);
                form.setFieldValue("accentColor", undefined);
                form.setFieldValue("textColor", undefined);
                setAccentColor(DEFAULT_ACCENT_COLOR);
                setTextColor(DEFAULT_TEXT_COLOR);
            }
        } else {
            form.setFieldValue("enableEmailDesign", false);
            form.setFieldValue("emailDesignTemplate", undefined);
            form.setFieldValue("header", undefined);
            form.setFieldValue("imageUrl", undefined);
            form.setFieldValue("accentColor", undefined);
            form.setFieldValue("textColor", undefined);
            setSelectedLogo("");
            setAccentColor(DEFAULT_ACCENT_COLOR);
            setTextColor(DEFAULT_TEXT_COLOR);
        }
    }

    const onCreate = async (values: any) => {
        const includeDesign = values.enableEmailDesign === true;
        const emailDesignTemplate = includeDesign ? values.emailDesignTemplate : undefined;
        const isClassicTemplate = emailDesignTemplate === EmailTemplateType.Classic;
        const isHeaderTemplate = emailDesignTemplate === EmailTemplateType.Header;
        const design = includeDesign
            ? {
                header: isHeaderTemplate ? values.header : undefined,
                imageUrl: isClassicTemplate ? values.imageUrl : undefined,
                emailDesignTemplate: emailDesignTemplate,
                textColor: isHeaderTemplate ? values.textColor : undefined,
                accentColor: isHeaderTemplate ? values.accentColor : undefined,
            }
            : undefined;

        try {
            await EmailTemplateService.EditTemplate({
                templateId: props.template.id,
                templateName: values.templateName,
                subject: values.subject,
                design: design,
                body: values.body,
                purpose: values.purpose,
                notificationEmail: values.notificationEmail
            });

        } catch (error) {
            message.warning(GetText("CreateTemplate_ErrorAlreadyExist"));
            return;
        }

        setOpen(false);
        props.reloadTemplate();
    }

    const onPurposeChange = (purpose: any) => {
        setPurpose(purpose);
        form.setFieldValue("purpose", purpose);
    }

    const clearForm = () => {
        form.resetFields();
        form.setFieldValue("enableEmailDesign", false);
        form.setFieldValue("sendWhenUserIsCreated", false);
        form.setFieldValue("purpose", undefined);
        setPurpose(undefined);
        setInitialHtml("");
        setSelectedLogo("");
        setAccentColor(DEFAULT_ACCENT_COLOR);
        setTextColor(DEFAULT_TEXT_COLOR);
    }

    return (
        <>
            {open ? <Modal
                width={900}
                open={open}
                title={props.isAutomation ? GetText("AutomaticSending_EditModalTitle") : GetText("EditTemplate_ModalHeader")}
                okText={GetText("EditTemplate_CreateText")}
                cancelText={GetText("EditTemplate_CancelText")}
                onOk={() => {
                    form
                        .validateFields()
                        .then((values) => {
                            onCreate(values);
                        })
                        .catch((info) => {
                            console.log('Validate Failed:', info);
                        });
                }}
                onCancel={onCancel}
            >
                <Form form={form} onFinish={onCreate} layout='vertical'>
                    <Col span={24}>
                        {props.isAutomation && <BookySelectDropDown
                            name="purpose"
                            label={GetText("AutomaticSending_EventLabel")}
                            handleChange={onPurposeChange}
                            placeholder={GetText("AutomaticSending_EventPlaceholder")}
                            options={AvailableEmailPurpose}
                            required
                            requiredMessage={GetText("CreateTemplate_RequiredText")}
                        />}

                        <BookyInput name="templateName" requiredMessage={GetText("CreateTemplate_RequiredText")} label={props.isAutomation ? GetText("AutomaticSending_NameLabel") : GetText("CreateTemplate_PlaceHolderText")} />
                        <BookyInput name="subject" requiredMessage={GetText("CreateTemplate_RequiredText")} label={GetText("CreateTemplate_Subject")} />
                        <OneFrontHtmlEditor initialHtml={initialHtml} enablePaymentLink={props.enablePaymentLink} onHtmlChange={onHtmlChange} label={GetText("CreateTemplate_Body")} name="body" requiredMessage={GetText("EmailForm_MessageRequired")} />

                        <BookyCheckbox alignLabelRight label={GetText("CreateTemplate_AddDesign")} name="enableEmailDesign" onChange={onEmailDesignToggle}></BookyCheckbox>

                        {hasEmailDesign ? <>
                            <Form.Item
                                label={GetText("CreateTemplate_DesignTemplate")}
                                name="emailDesignTemplate"
                                rules={[{ required: true, message: GetText("CreateTemplate_RequiredText") }]}
                            >
                                <EmailDesignTemplateCardSelect onTemplateSelected={onEmailDesignTemplateChange} />
                            </Form.Item>

                            {selectedEmailDesignTemplate === EmailTemplateType.Classic ? <>
                                <Form.Item
                                    name="imageUrl"
                                    hidden
                                    dependencies={["enableEmailDesign", "emailDesignTemplate"]}
                                    rules={[
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (getFieldValue("enableEmailDesign") && getFieldValue("emailDesignTemplate") === EmailTemplateType.Classic && !value) {
                                                    return Promise.reject(new Error(GetText("CreateTemplate_LogoRequired")));
                                                }

                                                return Promise.resolve();
                                            },
                                        }),
                                    ]}
                                >
                                    <Input />
                                </Form.Item>

                                <Form.Item label={GetText("CreateTemplate_Logo")}>
                                    <Button onClick={openImagePicker}>{GetText("CreateTemplate_SelectLogo")}</Button>
                                </Form.Item>

                                {selectedLogo ? <Image width={220} height={140} preview={true} src={selectedLogo}></Image> : ""}
                            </> : ""}

                            {selectedEmailDesignTemplate === EmailTemplateType.Header ? <>
                                <BookyInput name="header" requiredMessage={GetText("CreateTemplate_RequiredText")} label={GetText("CreateTemplate_Header")} />

                                <Form.Item label={GetText("CreateTemplate_AccentColor")}>
                                    <ColorPicker color={accentColor} onChangeComplete={onAccentColorChanged} />
                                </Form.Item>
                                <Form.Item hidden name="accentColor">
                                    <Input />
                                </Form.Item>

                                <Form.Item label={GetText("CreateTemplate_TextColor")}>
                                    <ColorPicker color={textColor} onChangeComplete={onTextColorChanged} />
                                </Form.Item>
                                <Form.Item hidden name="textColor">
                                    <Input />
                                </Form.Item>
                            </> : ""}
                        </> : ""}

                        {!props.isAutomation && <BookySelectDropDown helpTitle={GetText("CreateTemplate_TemplatePurposeTitleHelp")} help={GetText("CreateTemplate_TemplatePurposeHelp")} name="purpose" label={GetText("CreateTemplate_TemplatePurpose")} handleChange={onPurposeChange} placeholder={GetText("CreateTemplate_SelectPurposeEmail")} options={AvailableEmailPurpose} allowClear />}
                        {purpose === EmailTemplatePurpose.MemberGetsActivatedNotification && (
                            <BookyInput name="notificationEmail" validateEmailIfFilledIn requiredMessage={GetText("CreateTemplate_RequiredEmail")} label={GetText("CreateTemplate_NotificationEmail")} placeholder={GetText("CreateTemplate_TypeNotificationEmail")} />
                        )}

                    </Col>
                </Form>
            </Modal> : ""}

            <Modal
                title={GetText("MyEventDetails_SelectImage")}
                width={850}
                open={isImageModalOpen}
                onOk={() => { setIsImageModalOpen(false) }}
                onCancel={() => { setIsImageModalOpen(false) }}
            >
                <ImageSelect images={images} isPublicImage={true} loadImages={loadImages} onImageSelect={onImageSelect} selectedImage={selectedLogo} />
            </Modal>

            <EditOutlined
                style={{ fontSize: "20px" }}
                type="primary"
                onClick={() => {
                    setOpen(true);
                    setFieldsFromProps();
                }}
            >
            </EditOutlined>
        </>
    );
};

export default EditEmailTemplate;